import type {RootState} from '@/store';
import {createSelector} from '@reduxjs/toolkit';
import type {FabricObject} from '@postermywall/fabricjs-2';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getItemPageEndTime} from '@PosterWhiteboard/items/item/item-timing.library';
import {getAudioTimelineResizeBounds} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {getItemBarResizeBounds} from './timeline-bar-model';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';
import type {TimelineBarRef, TimelineGroupGestureMember, TimelineModifyingState, TimelineSelectionExtensionUids} from '../timeline.types';
import {TimelineItemType} from '../timeline.types';
import {setTimelineLastSelectionExtension, setTimelineSelectionAnchor} from '../timeline-reducer';
import type {TimelineBarClickOptions} from '../components/timeline-item/timeline-item.types';
import {TimelineMultiSelectType} from '@Libraries/ga-events';
import {trackTimelineMultiSelectOnceIfNeeded} from '@Libraries/poster-editor-tracking.library';

/**
 * The timeline's multi-bar selection, read from its two owners: canvas selection for
 * item bars (`activeItemIds`), AudioClips for audio bars. This module is the single
 * place that joins them; gestures never read selection directly — they snapshot it
 * once at gesture start via `captureTimelineGroupForGesture`.
 */

/** Whether a pointer/mouse interaction carries ANY selection modifier (shift / cmd / ctrl). */
export const hasMultiSelectModifier = (e: {shiftKey: boolean; metaKey: boolean; ctrlKey: boolean}): boolean => {
  return e.shiftKey || e.metaKey || e.ctrlKey;
};

/** Maps an interaction's modifier keys to the track selection policy's inputs: cmd/ctrl toggles the bar, shift extends the selection to it. */
export const getTimelineBarClickOptions = (e: {shiftKey: boolean; metaKey: boolean; ctrlKey: boolean}): TimelineBarClickOptions => {
  return {withToggleModifier: e.metaKey || e.ctrlKey, withExtendModifier: e.shiftKey};
};

const EMPTY_SELECTED_IDS: string[] = [];

/**
 * The canvas-selected ids that live on the item track — backgrounds and transcripts
 * have their own tracks and selection models. Memoized: subscribed components read it
 * on every store dispatch (30Hz during playback), so the filter must not recompute or
 * change identity unless the selection or the item map actually changed.
 */
export const getSelectedItemTrackIds = createSelector(
  [
    (state: RootState): string[] | undefined => state.posterEditor.activeItemIds[state.posterEditor.currentPageId],
    (state: RootState): Record<string, ItemObject> | undefined => state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId]?.items?.itemsHashMap,
  ],
  (activeItemIds, itemsHashMap): string[] => {
    if (!activeItemIds?.length || !itemsHashMap) {
      return EMPTY_SELECTED_IDS;
    }
    return activeItemIds.filter((uid) => {
      const item = itemsHashMap[uid];
      return item !== undefined && !item.isBackground && item.gitype !== ITEM_TYPE.TRANSCRIPT;
    });
  }
);

/** The audio side of the timeline selection — the slice mirror of AudioClips.selectedAudioItemUIDs. */
export const getSelectedAudioTrackIds = (state: RootState): string[] => {
  return state.posterEditor.selectedAudioItemUIDs;
};

export const isBarInTimelineSelection = (state: RootState, barId: string, barType: TimelineItemType): boolean => {
  if (barType === TimelineItemType.ITEM) {
    return getSelectedItemTrackIds(state).includes(barId);
  }
  if (barType === TimelineItemType.AUDIO) {
    return getSelectedAudioTrackIds(state).includes(barId);
  }
  return false;
};

interface TimelineExtendableBar extends TimelineBarRef {
  /** Row position in the timeline's visual stacking: item-track rows first (top), audio rows below them. */
  visualRow: number;
}

/** Every extend-selectable bar on the current page with its visual row — item-track rows stack above audio rows. */
const getExtendSelectableBars = (state: RootState): TimelineExtendableBar[] => {
  const currentPage = state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId];
  if (!currentPage) {
    return [];
  }
  const bars: TimelineExtendableBar[] = [];
  let maxItemRow = -1;
  for (const item of Object.values(currentPage.items.itemsHashMap)) {
    if (item.isBackground || item.gitype === ITEM_TYPE.TRANSCRIPT) {
      continue;
    }
    bars.push({id: item.uid, type: TimelineItemType.ITEM, visualRow: item.timelineSubRow});
    maxItemRow = Math.max(maxItemRow, item.timelineSubRow);
  }
  for (const audio of Object.values(currentPage.audioClips.audioItemsHashMap)) {
    bars.push({id: audio.uid, type: TimelineItemType.AUDIO, visualRow: maxItemRow + 1 + audio.timelineSubRow});
  }
  return bars;
};

/**
 * The bars a shift-click selects: every bar on every row between the anchor and the
 * target row, inclusive, in visual order. The anchor is the last plainly-clicked or
 * toggled bar; when it no longer resolves to a live bar, the first bar of the current
 * selection anchors instead. Returns undefined when no usable anchor exists (or the
 * target is gone) — the caller then degrades to a plain single-bar selection.
 */
const computeTimelineSelectionExtension = (state: RootState, targetId: string, targetType: TimelineItemType): TimelineSelectionExtensionUids | undefined => {
  const bars = getExtendSelectableBars(state);
  const target = bars.find((bar) => bar.id === targetId && bar.type === targetType);
  if (!target) {
    return undefined;
  }
  const anchorCandidates: TimelineBarRef[] = [
    ...(state.timeline?.selectionAnchor ? [state.timeline.selectionAnchor] : []),
    ...getSelectedItemTrackIds(state).map((uid): TimelineBarRef => ({id: uid, type: TimelineItemType.ITEM})),
    ...getSelectedAudioTrackIds(state).map((uid): TimelineBarRef => ({id: uid, type: TimelineItemType.AUDIO})),
  ];
  const anchor = anchorCandidates.map((candidate) => bars.find((bar) => bar.id === candidate.id && bar.type === candidate.type)).find((bar) => bar !== undefined);
  if (!anchor) {
    return undefined;
  }
  const lowRow = Math.min(anchor.visualRow, target.visualRow);
  const highRow = Math.max(anchor.visualRow, target.visualRow);
  const extendedOver = bars.filter((bar) => bar.visualRow >= lowRow && bar.visualRow <= highRow);
  return {
    itemUids: extendedOver.filter((bar) => bar.type === TimelineItemType.ITEM).map((bar) => bar.id),
    audioUids: extendedOver.filter((bar) => bar.type === TimelineItemType.AUDIO).map((bar) => bar.id),
  };
};

/**
 * The popup-menu target for a right-clicked timeline bar: the bar's own fabric object,
 * widened to the whole ActiveSelection when the bar is part of the canvas
 * multi-selection — so the popup routes to the group menu, the same menu a canvas
 * right-click on the selection gets. Audio bars have no fabric object (undefined).
 */
export const getTimelineBarContextTarget = (barId: string, barType: TimelineItemType): FabricObject | undefined => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  const target = barType === TimelineItemType.ITEM ? page?.items.itemsHashMap[barId]?.fabricObject : undefined;
  if (!target) {
    return undefined;
  }
  const activeObjects = page?.activeSelection.getActiveObjects() ?? [];
  if (activeObjects.length > 1 && activeObjects.includes(target)) {
    return page?.activeSelection.getActiveObject() ?? undefined;
  }
  return target;
};

/** Records the bar future shift-clicks extend from — every plain bar click and toggle click sets it. */
export const setTimelineSelectionAnchorForBar = (id: string, type: TimelineItemType): void => {
  window.PMW.redux.store.dispatch(setTimelineSelectionAnchor({id, type}));
};

/** Base ∪ extension, base order first — the selections a shift-click must carry over, then the bars it extends over. */
const unionUids = (baseUids: string[], extensionUids: string[]): string[] => {
  return [...baseUids, ...extensionUids.filter((uid) => !baseUids.includes(uid))];
};

/**
 * Applies a shift-click ending on the given bar: extends the selection to every bar on
 * every row between the anchor and the target across both multi-selectable tracks
 * (mixed selections included). Selections OUTSIDE the previous extension are preserved
 * — a cmd-clicked stray bar survives the shift-click — while the previous extension is
 * replaced, so successive shift-clicks re-extend (grow OR shrink) from the same anchor.
 * The anchor is deliberately NOT moved. With no usable anchor the shift-click degrades
 * to a plain single-bar selection of the target.
 */
export const extendTimelineSelection = (targetId: string, targetType: TimelineItemType): void => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!page) {
    return;
  }
  trackTimelineMultiSelectOnceIfNeeded(TimelineMultiSelectType.SHIFT_CLICK);
  const state = window.PMW.redux.store.getState();
  const extension = computeTimelineSelectionExtension(state, targetId, targetType);
  if (!extension) {
    if (targetType === TimelineItemType.ITEM) {
      const item = page.items.itemsHashMap[targetId];
      if (item) {
        page.activeSelection.setActiveObject(item.fabricObject);
      }
    } else if (targetType === TimelineItemType.AUDIO) {
      page.audioClips.selectAudioItem(targetId);
    }
    setTimelineSelectionAnchorForBar(targetId, targetType);
    return;
  }
  const lastExtension = state.timeline?.lastSelectionExtension;
  const baseItemUids = getSelectedItemTrackIds(state).filter((uid) => !lastExtension?.itemUids.includes(uid));
  const baseAudioUids = getSelectedAudioTrackIds(state).filter((uid) => !lastExtension?.audioUids.includes(uid));
  const itemUids = unionUids(baseItemUids, extension.itemUids);
  const items = itemUids.map((uid) => page.items.itemsHashMap[uid]).filter((item) => item !== undefined);
  page.activeSelection.setTimelineMultiSelection(items);
  page.audioClips.setSelectedAudioItems(unionUids(baseAudioUids, extension.audioUids));
  window.PMW.redux.store.dispatch(setTimelineLastSelectionExtension(extension));
};

/** The member matching a bar identity within a gesture snapshot. */
export const findTimelineGroupMember = (members: TimelineGroupGestureMember[], barId: string, barType: TimelineItemType): TimelineGroupGestureMember | undefined => {
  return members.find((member) => member.id === barId && member.type === barType);
};

/** One landing preview (drop ghost) on a track — the model-space rect the ghost renders from. */
export interface DropGhostBar {
  uid: string;
  startTime: number;
  endTime: number;
  subRow: number;
}

const NO_DROP_GHOST_BARS: DropGhostBar[] = [];

/**
 * The landing previews for an in-flight drag on `variant`'s track: one ghost per
 * dragged bar of that track. A single-bar drag ghosts the grabbed bar at the gesture's
 * live span; a group drag projects every same-track member by the shared time delta
 * and the grabbed bar's row delta — the same deltas the commit applies, so the ghosts
 * show exactly where the bars will land. A FOREIGN bar hovering this track on a
 * convert-on-drop drag (background → item) ghosts at the row the converted bar lands
 * on. Cross-track co-members get no ghost: their rows never change and their bars
 * already render at their landing span. Empty when no drag is targeting a row on
 * this track.
 */
export const getDropGhostBars = (modifying: TimelineModifyingState | undefined, variant: TimelineItemType): DropGhostBar[] => {
  if (modifying?.action.type !== 'dragging') {
    return NO_DROP_GHOST_BARS;
  }
  if (modifying.grabbed.type !== variant) {
    const foreignTarget = modifying.dropTarget;
    // A gap hover shows the row-insertion indicator instead of a ghost, mirroring own-track drags.
    if (foreignTarget?.trackType === variant && foreignTarget.insertionIndex === undefined) {
      return [{uid: modifying.grabbed.id, startTime: modifying.startTime, endTime: modifying.endTime, subRow: foreignTarget.row}];
    }
    return NO_DROP_GHOST_BARS;
  }
  if (modifying.targetSubRow === undefined) {
    return NO_DROP_GHOST_BARS;
  }
  const members = modifying.groupMembers;
  const grabbedMember = members ? findTimelineGroupMember(members, modifying.grabbed.id, modifying.grabbed.type) : undefined;
  if (!members || !grabbedMember) {
    return [{uid: modifying.grabbed.id, startTime: modifying.startTime, endTime: modifying.endTime, subRow: modifying.targetSubRow}];
  }
  const timeDelta = modifying.startTime - grabbedMember.originalStartTime;
  const rowDelta = modifying.targetSubRow - grabbedMember.originalSubRow;
  return members
    .filter((member) => member.type === variant)
    .map((member): DropGhostBar => {
      return {
        uid: member.id,
        startTime: member.originalStartTime + timeDelta,
        endTime: member.originalEndTime + timeDelta,
        subRow: member.originalSubRow + rowDelta,
      };
    });
};

/** Splits a gesture snapshot into the per-owner uid lists the page orchestrators take. */
export const splitTimelineGroupMemberUids = (members: TimelineGroupGestureMember[]): {itemUids: string[]; audioUids: string[]} => {
  return {
    itemUids: members.filter((member) => member.type === TimelineItemType.ITEM).map((member) => member.id),
    audioUids: members.filter((member) => member.type === TimelineItemType.AUDIO).map((member) => member.id),
  };
};

/**
 * Snapshots the multi-bar selection for a gesture that just started on `grabbedId`:
 * every member's id, kind, span, row, and resize bounds. Returns undefined when the
 * grabbed bar is not part of a selection of two or more bars — the gesture stays a
 * single-bar one and none of the group machinery engages.
 */
export const captureTimelineGroupForGesture = (state: RootState, grabbedId: string, grabbedType: TimelineItemType): TimelineGroupGestureMember[] | undefined => {
  if (grabbedType !== TimelineItemType.ITEM && grabbedType !== TimelineItemType.AUDIO) {
    return undefined;
  }
  const currentPage = state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId];
  if (!currentPage) {
    return undefined;
  }

  const members: TimelineGroupGestureMember[] = [];
  for (const uid of getSelectedItemTrackIds(state)) {
    const itemObject = currentPage.items.itemsHashMap[uid];
    members.push({
      id: uid,
      type: TimelineItemType.ITEM,
      originalStartTime: itemObject.onPageStartTime,
      originalEndTime: getItemPageEndTime(itemObject),
      originalSubRow: itemObject.timelineSubRow,
      resizeBounds: getItemBarResizeBounds(itemObject),
    });
  }
  for (const uid of getSelectedAudioTrackIds(state)) {
    const audioObject = currentPage.audioClips.audioItemsHashMap[uid];
    if (!audioObject) {
      continue;
    }
    const span = {startTime: audioObject.onPageStartTime, endTime: audioObject.onPageStartTime + getPlaybackDuration(audioObject.audioPlayer)};
    members.push({
      id: uid,
      type: TimelineItemType.AUDIO,
      originalStartTime: span.startTime,
      originalEndTime: span.endTime,
      originalSubRow: audioObject.timelineSubRow,
      resizeBounds: getAudioTimelineResizeBounds(span, audioObject.audioPlayer),
    });
  }

  const isGrabbedBarInSelection = members.some((member) => member.id === grabbedId && member.type === grabbedType);
  return members.length >= 2 && isGrabbedBarInSelection ? members : undefined;
};
